import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Table, Button, Tag, Empty, Space, Tooltip } from "antd";
import { EyeOutlined, DownloadOutlined } from "@ant-design/icons";

import { ComplianceRow } from "../../../services/Compliance/ComplianceApis";
import {
  getDocumentLabel,
  RequiredDocumentKey,
  ComplianceCardKey,
  COMPLIANCE_CARD_META,
} from "../../../utils/documentCompliance";

import "../../../styles/ComplianceModal.css";

interface IModalProps {
  show: boolean;
  showModal: Function;
  cardKey: ComplianceCardKey;
  title: string;
  members: ComplianceRow[];
}

const csvEscape = (value: any) => {
  const text = String(value ?? "");
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const outstandingFromDetail = (detail?: string): string | null => {
  if (!detail) return null;
  const match = String(detail).match(/Outstanding\s+R?\s*([\d][\d\s.,]*)/i);
  if (!match) return null;
  const raw = match[1].replace(/\s/g, "").replace(/,/g, "");
  const n = Number(raw);
  if (!Number.isFinite(n)) return `R${match[1].trim()}`;
  return `R${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const renderIssueDetail = (
  value: string | undefined,
  row: ComplianceRow,
  isDebiCheckCard: boolean
) => {
  const text = value || "-";
  const missingMatch = text.match(/^Missing:\s*(.+)$/i);
  const parts = missingMatch
    ? missingMatch[1]
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean)
    : null;

  return (
    <Space size={[4, 4]} wrap>
      {isDebiCheckCard && row.Severity === "error" && (
        <Tag color="red">Error</Tag>
      )}
      {isDebiCheckCard && row.Severity === "pending" && (
        <Tag color="blue">Pending</Tag>
      )}
      {parts ? (
        parts.map((part) => (
          <Tag key={part} color="orange">
            {part}
          </Tag>
        ))
      ) : (
        <span>{text}</span>
      )}
    </Space>
  );
};

const MIN_MODAL_WIDTH = 360;
const MODAL_PAD = 48;
const VIEWPORT_GUTTER = 32;

const NonCompliantMembersModal: React.FC<IModalProps> = ({
  show,
  showModal,
  cardKey,
  title,
  members,
}) => {
  const navigate = useNavigate();
  const bodyRef = useRef<HTMLDivElement>(null);
  const [modalWidth, setModalWidth] = useState<number>(MIN_MODAL_WIDTH);
  const [constrained, setConstrained] = useState(false);

  const isClaimCard = COMPLIANCE_CARD_META[cardKey]?.Entity === "claim";
  const isDocumentCard =
    cardKey === "IdDocument" ||
    cardKey === "ApplicationDocument" ||
    cardKey === "PolicyDocument" ||
    cardKey === "AnyMissingDocs";
  const isPendingLapseCard = cardKey === "PendingLapse";
  const isLapseRiskCard = cardKey === "LapseRisk";
  const isDebiCheckCard = cardKey === "DebiCheckIncomplete";

  const sortedMembers = isDebiCheckCard
    ? [...members].sort((a, b) => {
        const rank = (s?: string | null) =>
          s === "error" ? 0 : s === "pending" ? 1 : 2;
        return rank(a.Severity) - rank(b.Severity);
      })
    : members;

  const fitModalToContent = useCallback(() => {
    const root = bodyRef.current;
    if (!root) return;
    const table = root.querySelector(
      ".compliance-members-table table"
    ) as HTMLTableElement | null;
    if (!table) return;

    const maxW = Math.max(320, window.innerWidth - VIEWPORT_GUTTER);
    const minW = Math.min(MIN_MODAL_WIDTH, maxW);

    const modalEl = root.closest(".compliance-members-modal");
    const wasConstrained = modalEl?.classList.contains(
      "compliance-members-modal--constrained"
    );
    modalEl?.classList.remove("compliance-members-modal--constrained");
    modalEl?.classList.add("compliance-members-modal--measuring");

    const titleText = modalEl?.querySelector(
      ".compliance-modal-title-text"
    ) as HTMLElement | null;
    const exportBtn = modalEl?.querySelector(
      ".compliance-export-btn"
    ) as HTMLElement | null;
    const headerIntrinsic =
      (titleText?.scrollWidth ?? 0) +
      (exportBtn?.offsetWidth ?? 0) +
      80;

    const needed = Math.ceil(
      Math.max(table.scrollWidth, headerIntrinsic) + MODAL_PAD
    );

    modalEl?.classList.remove("compliance-members-modal--measuring");
    if (wasConstrained) {
      modalEl?.classList.add("compliance-members-modal--constrained");
    }

    const next = Math.min(Math.max(needed, minW), maxW);
    setModalWidth((w) => (w === next ? w : next));
    setConstrained(needed > maxW);
  }, []);

  useLayoutEffect(() => {
    if (!show) return;
    fitModalToContent();
    const raf = window.requestAnimationFrame(fitModalToContent);
    const t = window.setTimeout(fitModalToContent, 100);
    window.addEventListener("resize", fitModalToContent);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t);
      window.removeEventListener("resize", fitModalToContent);
    };
  }, [show, sortedMembers, cardKey, fitModalToContent]);

  const handleClose = () => showModal();

  const handleView = (row: ComplianceRow) => {
    navigate(row.ViewPath || `/members/${row.MemberID ?? row.id}`);
  };

  const viewLabel = isPendingLapseCard
    ? "Open run"
    : isClaimCard
      ? "View Claim"
      : "View Member";

  const handleExport = () => {
    const headers = [
      "ID Number",
      "Policy Number",
      "Name",
      "Surname",
      "Product",
      ...(isLapseRiskCard
        ? ["Months behind", "Outstanding"]
        : ["Age (days)", "Detail"]),
      ...(isClaimCard ? ["Claim Number"] : []),
    ];
    const lines = [
      headers.join(","),
      ...members.map((row) =>
        [
          row.IdNumber,
          row.PolicyNumber,
          row.Name,
          row.Surname,
          row.ProductName,
          ...(isLapseRiskCard
            ? [row.MonthsBehind ?? "", outstandingFromDetail(row.Detail) ?? ""]
            : [row.AgeDays ?? "", row.Detail]),
          ...(isClaimCard ? [row.ClaimNumber ?? ""] : []),
        ]
          .map(csvEscape)
          .join(",")
      ),
    ];
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${title.replace(/\s+/g, "-").toLowerCase()}-compliance.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const actionColumn = {
    title: "",
    key: "action",
    width: 48,
    align: "center" as const,
    render: (_: any, row: ComplianceRow) => (
      <Tooltip title={viewLabel}>
        <Button
          type="primary"
          size="small"
          className="compliance-view-btn"
          aria-label={viewLabel}
          icon={<EyeOutlined />}
          onClick={() => handleView(row)}
        />
      </Tooltip>
    ),
  };

  const columns: any[] = isPendingLapseCard
    ? [
        {
          title: "Lapse run",
          dataIndex: "Name",
          render: (value: string) => value || "-",
        },
        {
          title: "Owning group",
          dataIndex: "ProductName",
          render: (value: string) => value || "-",
        },
        {
          title: "Issue",
          dataIndex: "Detail",
          render: (value: string) => value || "-",
        },
        actionColumn,
      ]
    : [
        {
          title: "Policy",
          dataIndex: "PolicyNumber",
          render: (value: string, row: ComplianceRow) => (
            <div className="compliance-stack">
              <span className="compliance-primary">{value || "-"}</span>
              {row.IdNumber ? (
                <span className="compliance-muted">{row.IdNumber}</span>
              ) : null}
            </div>
          ),
        },
        {
          title: "Member",
          key: "member",
          render: (_: any, row: ComplianceRow) =>
            [row.Name, row.Surname].filter(Boolean).join(" ") || "-",
        },
        {
          title: "Product",
          dataIndex: "ProductName",
          render: (value: string) => value || "-",
        },
        ...(isClaimCard
          ? [
              {
                title: "Claim",
                dataIndex: "ClaimNumber",
                render: (value: string) => value || "-",
              },
            ]
          : []),
        ...(isLapseRiskCard
          ? [
              {
                title: "Behind",
                dataIndex: "MonthsBehind",
                align: "right" as const,
                sorter: (a: ComplianceRow, b: ComplianceRow) =>
                  (a.MonthsBehind ?? -1) - (b.MonthsBehind ?? -1),
                defaultSortOrder: "descend" as const,
                render: (value: number | null | undefined) =>
                  value == null ? "-" : `~${value}`,
              },
              {
                title: "Outstanding",
                key: "outstanding",
                align: "right" as const,
                render: (_: any, row: ComplianceRow) =>
                  outstandingFromDetail(row.Detail) || "-",
              },
            ]
          : [
              {
                title: "Age",
                dataIndex: "AgeDays",
                align: "right" as const,
                sorter: (a: ComplianceRow, b: ComplianceRow) =>
                  (a.AgeDays ?? -1) - (b.AgeDays ?? -1),
                defaultSortOrder: "descend" as const,
                render: (value: number | null) =>
                  value == null ? "-" : value,
              },
              {
                title: "Issue",
                dataIndex: "Detail",
                render: (value: string, row: ComplianceRow) =>
                  renderIssueDetail(value, row, isDebiCheckCard),
              },
            ]),
        ...(isDocumentCard
          ? [
              {
                title: "Also missing",
                dataIndex: "MissingDocuments",
                render: (missing: RequiredDocumentKey[] = []) => {
                  const others =
                    cardKey === "AnyMissingDocs"
                      ? missing
                      : missing.filter((key) => key !== cardKey);
                  if (others.length === 0) return "-";
                  return (
                    <Space size={[4, 4]} wrap={false}>
                      {others.map((key) => (
                        <Tag color="orange" key={key}>
                          {getDocumentLabel(key)}
                        </Tag>
                      ))}
                    </Space>
                  );
                },
              },
            ]
          : []),
        actionColumn,
      ];

  const modalTitle = (
    <div className="compliance-modal-title-row">
      <span className="compliance-modal-title-text">
        {title} ({sortedMembers.length})
      </span>
      <Button
        type="primary"
        size="small"
        className="compliance-export-btn"
        icon={<DownloadOutlined />}
        onClick={handleExport}
        disabled={!sortedMembers.length}
      >
        Export CSV
      </Button>
    </div> 
  );

  return (
    <Modal
      className={`compliance-members-modal${
        constrained ? " compliance-members-modal--constrained" : ""
      }`}
      title={modalTitle}
      open={show}
      onCancel={handleClose}
      width={modalWidth}
      centered
      footer={null}
      destroyOnClose
      bodyStyle={{
        paddingTop: 8,
        maxHeight: "90vh",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <div ref={bodyRef} className="compliance-modal-body">
        <Table
          className="compliance-members-table"
          rowKey={(record) => String(record.id)}
          columns={columns}
          dataSource={sortedMembers}
          size="small"
          pagination={{
            pageSize: 10,
            hideOnSinglePage: true,
            size: "small",
            showSizeChanger: false,
          }}
          locale={{
            emptyText: (
              <Empty description="Nothing outstanding for this check" />
            ),
          }}
        />
      </div>
    </Modal>
  );
};

export default NonCompliantMembersModal;
